import LayoutRenderer from "./LayoutRenderer";
import BlockNode from "./Nodes/BlockNode";
import ColumnNode from "./Nodes/ColumnNode";
import ElementNode from "./Nodes/ElementNode";
import FullNode from "./Nodes/FullNode";
import SectionNode from "./Nodes/SectionNode";
import TabNode from "./Nodes/TabNode";
import { getCustomClassName, getNodeClassName, getNodeStyle } from "./nodeFunc";

const NodeRenderer = ({ node, parentId, ...nodeRendererProps }) => {
  const {
    draggedItem,
    handleDragOver,
    handleDragStart,
    handleResizeStart,
    handleOnDrop,
    hoveredItemId,
    handleClick,
    handleRightClick,
    selectedItem,
    type,
    saveConfigOfNode,
    onClickElement,
    device,
    setHoveredDirectItemId,
    hoveredDirectItemId,
    multiSelectedItems
  } = nodeRendererProps;

  const isEditable = type === "editable";
  const isHovered = node.id === hoveredItemId;
  const isDirectHovered = node.id === hoveredDirectItemId;
  const isSelectedItem = node.id === selectedItem?.id || (multiSelectedItems && multiSelectedItems[node.id]);

  const handleMouseOver = (e) => {
    e.stopPropagation();
    setHoveredDirectItemId(node.id);
  };

  const handleMouseOut = (e) => {
    e.stopPropagation();
    if (hoveredDirectItemId === node.id) {
      setHoveredDirectItemId(null);
    }
  };

  const handleClickWrapper = (e) => handleClick(e, node, parentId);
  const handleRightClickWrapper = (e) => handleRightClick(e, node, parentId);
  const handleDragStartWrapper = (e) => handleDragStart(e, node, parentId);
  const handleDragOverWrapper = (e) => handleDragOver(e, node, parentId);
  const handleDropWrapper = (e) => handleOnDrop(e, node, parentId);

  const renderLayout = (layoutConfig) => {
    return <LayoutRenderer layoutConfig={layoutConfig} parentId={node.id} {...nodeRendererProps} />;
  };

  const className = getNodeClassName({
    node,
    isEditable,
    isHovered,
    isDirectHovered,
    isSelectedItem,
    draggedItem
  });

  const newNodeStyle = getNodeStyle(node);

  const resizeHandler = (node) => {
    if (isEditable && selectedItem?.id === node.id) {
      return <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, node, parentId)} />;
    }
    return null;
  };

  const applyEditableHandler = (handler) => (isEditable ? handler : undefined);
  const applyTemplateHandler = (handler) => (isEditable || type === "template_selector" ? handler : undefined);

  const hoveredHandlerProps = {
    onMouseOver: applyTemplateHandler(handleMouseOver),
    onMouseOut: applyTemplateHandler(handleMouseOut)
  };

  const onClickHandlerProps = {
    onClick: applyTemplateHandler(handleClickWrapper)
  };

  const interactionHandlers = {
    onDragOver: applyEditableHandler(handleDragOverWrapper),
    onDrop: applyEditableHandler(handleDropWrapper),
    onDragLeave: applyEditableHandler((e) => e.stopPropagation())
  };

  const draggableHandler = {
    draggable: isEditable,
    onDragStart: applyEditableHandler(handleDragStartWrapper)
  };

  const onContextMenuHandlerProps = {
    onContextMenu: applyTemplateHandler(handleRightClickWrapper)
  };

  switch (node.type) {
    case "full":
      return (
        <FullNode
          node={node}
          className={className}
          style={newNodeStyle}
          handlers={{
            ...interactionHandlers,
            ...hoveredHandlerProps
          }}
          renderLayout={renderLayout}
        />
      );
    case "section":
      return (
        <SectionNode
          node={node}
          className={className}
          style={newNodeStyle}
          handlers={{
            ...interactionHandlers,
            ...hoveredHandlerProps,
            ...onClickHandlerProps,
            ...onContextMenuHandlerProps
          }}
          renderLayout={renderLayout}
          resizeHandle={resizeHandler}
        />
      );
    case "column":
      return (
        <ColumnNode
          node={node}
          className={className}
          style={newNodeStyle}
          handlers={{
            ...interactionHandlers,
            ...hoveredHandlerProps,
            ...onClickHandlerProps,
            ...onContextMenuHandlerProps,
            ...draggableHandler
          }}
          renderLayout={renderLayout}
          resizeHandle={resizeHandler}
        />
      );
    case "block":
      return (
        <BlockNode
          node={node}
          className={className}
          style={newNodeStyle}
          handlers={{
            ...interactionHandlers,
            ...hoveredHandlerProps,
            ...onClickHandlerProps,
            ...onContextMenuHandlerProps,
            ...draggableHandler
          }}
          renderLayout={renderLayout}
          resizeHandle={resizeHandler}
        />
      );
    case "element":
      return (
        <ElementNode
          node={node}
          className={getCustomClassName({ node, isEditable, isHovered, isDirectHovered, isSelectedItem, draggedItem })} // no node class names here
          style={newNodeStyle}
          resizeHandle={resizeHandler}
          handlers={{
            ...interactionHandlers,
            ...hoveredHandlerProps,
            ...onClickHandlerProps,
            ...onContextMenuHandlerProps,
            ...draggableHandler
          }}
          type={type}
          saveConfigOfNode={saveConfigOfNode}
          onClickElement={onClickElement}
          device={device}
        />
      );
    case "tab":
      return (
        <TabNode
          node={node}
          className={className}
          style={newNodeStyle}
          handlers={{
            ...interactionHandlers,
            ...hoveredHandlerProps,
            ...onClickHandlerProps,
            ...onContextMenuHandlerProps,
            ...draggableHandler
          }}
          renderLayout={renderLayout}
          resizeHandle={resizeHandler}
        />
      );
    default:
      return null;
  }
};
export default NodeRenderer;
